import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Form,
  Input,
  Select,
  Button,
  message,
  Modal,
  Space,
  Drawer,
  DatePicker,
  Tag,
  Tooltip,
  Radio,
  Dropdown,
} from "antd";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { valveService } from "../../services/valve.service";
import { useNavigate } from "react-router-dom";
import { userService } from "../../services/user.service";
import FilterForm from "../../components/FilterForm";
import PasteImage from "../../components/PasteImage";
import dayjs from "dayjs";
import "./Valves.scss";
import { DownOutlined } from "@ant-design/icons";

function Valves() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [exportFileName, setExportFileName] = useState("");
  const [userType, setUserType] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(2025);
  const [batchMode, setBatchMode] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isBatchStatusModalVisible, setIsBatchStatusModalVisible] =
    useState(false);
  const [isUpdateAllModalVisible, setIsUpdateAllModalVisible] = useState(false);
  const [batchStatus, setBatchStatus] = useState(null);
  const [currentSearchFilters, setCurrentSearchFilters] = useState(null);

  const exportMenuItems = [
  {
    key: 'pdf',
    label: "PDF",
  },
  {
    key: 'word',
    label: "Word",
  },
];

  const valveTypes = [
    { value: 1, label: "闸阀" },
    { value: 2, label: "蝶阀" },
  ];

  const wellCoverLocations = [
    { value: 1, label: "机动车道" },
    { value: 2, label: "非机动车道" },
    { value: 3, label: "人行横道" },
    { value: 4, label: "人行道" },
    { value: 7, label: "泥土路面" },
    { value: 5, label: "绿化带" },
  ];

  const valveCategories = [
    { value: 1, label: "小区进水阀门" },
    { value: 4, label: "小区单元阀门" },
    { value: 2, label: "小区环线阀门" },
    { value: 5, label: "小区消火栓阀门" },
    { value: 3, label: "小区楼幢阀门" },
    { value: 7, label: "市政管线阀门" },
    { value: 8, label: "市政管线消防栓阀门" },
    { value: 9, label: "小区泵房进水阀" },
    { value: 6, label: "小区其他" },
  ];

  const valveStatuses = [
    { value: 1, label: "待普查" },
    { value: 2, label: "普查中" },
    { value: 3, label: "已普查（阀门正常）" },
    { value: 4, label: "异常挂起" },
    { value: 5, label: "已签证" },
    { value: 6, label: "已结算" },
    { value: 7, label: "已挂接" },
  ];

  const wellCoverStatuses = [
    { value: 0, label: "破损" },
    { value: 1, label: "完好" },
  ];

  const cylbStatuses = [
    { value: 0, label: "开井查验" },
    { value: 1, label: "不开井查验" },
  ];

  const wellCoverAbnormalDesc = [
    { value: 1, label: "无" },
    { value: 2, label: "GIS有，现场灭失" },
    { value: 3, label: "GIS有，现场错位" },
    { value: 4, label: "GIS无，现场有" },
  ];

  const checkTypes = [
    { value: 0, label: "开井查验" },
    { value: 1, label: "不开井查验" },
  ];

  const initialLoadRef = useRef(true);

  const fetchValves = React.useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const response = await valveService.getValveList({
          page: params.page || currentPage,
          limit: params.limit || pageSize,
          subtype: params.subtype || params.type,
          address: params.address,
          pc_date: params.pc_date,
          pc_date_start: params.pc_dateRange?.[0],
          pc_date_end: params.pc_dateRange?.[1].endOf("day"),
          geometry_x: params.geometry_x,
          geometry_y: params.geometry_y,
          geometry_z: params.geometry_z,
          fmlsh: params.fmlsh,
          status: Array.isArray(params.status)
            ? JSON.stringify(params.status)
            : undefined,
          category: Array.isArray(params.category)
            ? JSON.stringify(params.category)
            : undefined,
          jgbh: params.jgbh,
          jgzt: params.jgzt,
          jgzb: params.jgzb,
          jgycms: Array.isArray(params.jgycms)
            ? JSON.stringify(params.jgycms)
            : undefined,
          cylb: params.cylb,
          version: params.version || currentVersion,
          is_deleted: 0,
        });

        if (response.code === 0) {
          setData(
            response.data.list.map((item) => ({ ...item, key: item.id }))
          );
          setTotal(response.data.total);
          // 保存筛选条件和分页状态
          saveFilterToStorage(
            params,
            params.page || currentPage,
            params.limit || pageSize
          );
        } else {
          message.error(response.message || "获取阀门列表失败");
        }
      } catch (error) {
        message.error(
          "获取阀门列表失败：" + error.response?.data?.message || error.message
        );
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize, currentVersion]
  );

  useEffect(() => {
    // 只在组件初始加载时执行，分页切换时不执行
    if (!initialLoadRef.current) return;

    // 检查是否从详情页返回，如果是则恢复筛选条件和分页状态
    const checkPreviousPage = () => {
      try {
        // 获取前置页面信息
        const previousPageData = sessionStorage.getItem("previousPage");
        // 输出前置页面信息
        if (previousPageData) {
          const previousPage = JSON.parse(previousPageData);
          // 检查是否从阀门详情页返回
          if (previousPage.url && previousPage.url.includes("/admin/valves/")) {
            restoreFilterFromStorage();
          } else {
            fetchValves();
          }
        } else {
          fetchValves();
        }

        // 记录当前页面，用于下次导航时作为上一页
        sessionStorage.setItem(
          "previousPage",
          JSON.stringify({
            url: window.location.pathname + window.location.search,
            timestamp: new Date().getTime(),
          })
        );
      } catch (error) {
        console.error("处理页面导航历史时出错:", error);
        fetchValves();
      }
    };

    checkPreviousPage();
    verifyUser();
    initialLoadRef.current = false;
  }, []);

  const handleDispatch = async (
    id,
    creator_id,
    assign_id,
    gis_pic,
    location_pic
  ) => {
    try {
      const response = await valveService.createTicket(
        id,
        creator_id,
        assign_id,
        gis_pic,
        location_pic
      );

      if (response.code === 0) {
        message.success("派单成功");
        fetchValves();
      } else {
        message.error(response.message || "派单失败");
      }
    } catch (error) {
      message.error(
        "派单失败：" + error.response?.data?.message || error.message
      );
    }
  };

  const handleCancel = async (id) => {
    Modal.confirm({
      title: "确认撤单",
      content: "确定要撤销这个工单吗？",
      okText: "确认",
      cancelText: "取消",
      onOk: async () => {
        try {
          const response = await valveService.revokeTicket(id);

          if (response.code === 0) {
            message.success("撤单成功");
            // 获取最新的阀门数据
            const valveResponse = await valveService.getValveDetail(id);
            if (valveResponse.code === 0) {
              // 更新表格中对应行的数据
              setData(
                data.map((item) =>
                  item.id === id ? { ...valveResponse.data, key: id } : item
                )
              );
            }
          } else {
            message.error(response.message || "撤单失败");
          }
        } catch (error) {
          message.error(
            "撤单失败：" + error.response?.data?.message || "未知错误"
          );
        }
      },
    });
  };

  const verifyUser = async () => {
    try {
      const result = await userService.verifyUser();
      if (result.success) {
        setUserType(result.data.type);
        setCurrentUser(result.data);
      }
    } catch (error) {
      message.error("验证用户身份失败");
    }
  };

  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [currentValve, setCurrentValve] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [hideCoordinates, setHideCoordinates] = useState(false);
  const infoCardRef = useRef(null);

  const columns = [
    {
      title: "阀门ID",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 80,
    },
    {
      title: "GIS流水号",
      dataIndex: "fmlsh",
      key: "fmlsh",
      fixed: "left",
      width: 120,
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      fixed: "left",
      width: 100,
      render: (status) => {
        let color = "";
        let statusText = "";

        switch (Number(status)) {
          case 1:
            color = "#339DE2";
            statusText = "待普查";
            break;
          case 2:
            color = "#FBB531";
            statusText = "普查中";
            break;
          case 3:
            color = "#79CE7E";
            statusText = "已普查";
            break;
          case 4:
            color = "#EB5F5D";
            statusText = "异常挂起";
            break;
          case 5:
            color = "#B18D80";
            statusText = "已签证";
            break;
          case 6:
            color = "#9AB4BF";
            statusText = "已结算";
            break;
          case 7:
            color = "#0D4480";
            statusText = "已挂接";
            break;
          default:
            color = "#d9d9d9";
            statusText = "未知";
        }

        return (
          <Tag color={color} style={{ minWidth: "30px", textAlign: "center" }}>
            {statusText}
          </Tag>
        );
      },
    },
    {
      title: "普查日期",
      dataIndex: "pc_date",
      key: "pc_date",
      width: 100,
      render: (text) =>
        text ? new Date(text).toLocaleDateString("zh-CN") : "-",
    },
    {
      title: "地址",
      dataIndex: "address",
      key: "address",
      width: 150,
      ellipsis: {
        showTitle: false,
      },
      render: (address) => (
        <Tooltip placement="topLeft" title={address}>
          {address}
        </Tooltip>
      ),
    },
    {
      title: "X坐标",
      dataIndex: "geometry_x",
      key: "geometry_x",
      width: 130,
      render: (value) => (value ? Number(value).toFixed(3) : "-"),
    },
    {
      title: "Y坐标",
      dataIndex: "geometry_y",
      key: "geometry_y",
      width: 120,
      render: (value) => (value ? Number(value).toFixed(3) : "-"),
    },
    {
      title: "Z坐标",
      dataIndex: "geometry_z",
      key: "geometry_z",
      width: 120,
      render: (value) => (value ? Number(value).toFixed(3) : "-"),
    },
    {
      title: "阀门深度",
      dataIndex: "depth",
      key: "depth",
      width: 90,
      render: (value) => (value ? Number(value).toFixed(2) + "米" : "-"),
    },
    {
      title: "口径",
      dataIndex: "standard",
      key: "standard",
      width: 80,
      render: (value) => (value ? "DN" + Number(value) : "-"),
    },
    {
      title: "GIS流水号",
      dataIndex: "fmlsh",
      key: "fmlsh",
      width: 100,
      ellipsis: true,
      render: (value) => value || "-",
    },
    {
      title: "阀门类别",
      dataIndex: "category",
      key: "category",
      width: 120,
      ellipsis: {
        showTitle: false,
      },
      render: (category) => {
        const item = valveCategories.find(
          (item) => item.value === Number(category)
        );
        const text = item ? item.label : "-";
        return (
          <Tooltip placement="topLeft" title={text}>
            {text}
          </Tooltip>
        );
      },
    },
    {
      title: "井盖编号",
      dataIndex: "jgbh",
      key: "jgbh",
      width: 100,
      ellipsis: {
        showTitle: false,
      },
      render: (value) => (
        <Tooltip placement="topLeft" title={value || "-"}>
          {value || "-"}
        </Tooltip>
      ),
    },
    {
      title: "井盖异常描述",
      dataIndex: "jgycms",
      key: "jgycms",
      width: 120,
      ellipsis: {
        showTitle: false,
      },
      render: (desc) => {
        const item = wellCoverAbnormalDesc.find(
          (item) => item.value === Number(desc)
        );
        const text = item ? item.label : "-";
        return (
          <Tooltip placement="topLeft" title={text}>
            {text}
          </Tooltip>
        );
      },
    },
    {
      title: "查验类别",
      dataIndex: "cylb",
      key: "cylb",
      width: 120,
      render: (type) => {
        if (type === null) {
          return "-";
        } else {
          const item = cylbStatuses.find((item) => item.value === Number(type));
          return item ? item.label : "-";
        }
      },
    },
    {
      title: "审核内业",
      dataIndex: ["auditor", "nickname"],
      key: "auditor",
      width: 90,
      ellipsis: { showTitle: false },
      render: (value) => (
        <Tooltip placement="topLeft" title={value || "-"}>
          {value || "-"}
        </Tooltip>
      ),
    },
    {
      title: "审核时间",
      dataIndex: "audit_time",
      key: "audit_time",
      width: 130,
      ellipsis: { showTitle: false },
      render: (text) => {
        const formattedTime = text
          ? new Date(text).toLocaleString("zh-CN", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "-";
        return (
          <Tooltip placement="topLeft" title={formattedTime}>
            {formattedTime}
          </Tooltip>
        );
      },
    },
    {
      title: "录入外业",
      dataIndex: ["worker", "nickname"],
      key: "worker",
      width: 90,
      ellipsis: { showTitle: false },
      render: (value) => (
        <Tooltip placement="topLeft" title={value || "-"}>
          {value || "-"}
        </Tooltip>
      ),
    },
    {
      title: "录入时间",
      dataIndex: "work_time",
      key: "work_time",
      width: 130,
      ellipsis: { showTitle: false },
      render: (text) => {
        const formattedTime = text
          ? new Date(text).toLocaleString("zh-CN", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "-";
        return (
          <Tooltip placement="topLeft" title={formattedTime}>
            {formattedTime}
          </Tooltip>
        );
      },
    },
    {
      title: "备注",
      dataIndex: "memo",
      key: "memo",
      width: 120,
      ellipsis: {
        showTitle: false,
      },
      render: (value) => (
        <Tooltip placement="topLeft" title={value || "-"}>
          {value || "-"}
        </Tooltip>
      ),
    },
    {
      title: "操作",
      key: "action",
      fixed: "right",
      width: 280,
      render: (_, record) => {
        const status = Number(record.status);
        return (
          (userType === 0 || userType === 1) && (
            <Space size="small">
              <Tooltip
                title={
                  status === 2 ? "若要调整阀门，请先撤销工单后，再进行调整" : ""
                }
              >
                <Button
                  size="small"
                  type="link"
                  onClick={() => navigate(`/admin/valves/${record.id}`)}
                  disabled={status === 2}
                >
                  编辑
                </Button>
              </Tooltip>
              <Button
                size="small"
                type="link"
                onClick={() => handleShowInfoCard(record)}
              >
                生成信息卡
              </Button>
              {(status === 1 || status === 4) && (
                <Button
                  size="small"
                  type="link"
                  onClick={() => {
                    setDispatchValve(record);
                    setIsDispatchModalVisible(true);
                  }}
                >
                  派单
                </Button>
              )}
              {status === 2 && (
                <Button
                  size="small"
                  type="link"
                  onClick={() => handleCancel(record.id)}
                >
                  撤单
                </Button>
              )}
              {userType === 0 && (
                <Button
                  type="link"
                  danger
                  onClick={() => handleDelete(record.id)}
                  size="small"
                >
                  删除
                </Button>
              )}
            </Space>
          )
        );
      },
    },
  ];

  const handleShowInfoCard = (record) => {
    setCurrentValve(record);
    setIsDrawerVisible(true);
  };

  // 导出信息卡
  const handleExportInfoCard = async (event) => {
    if (event.key === "pdf") {
      await handleExportInfoCardPdf();
    }
    if (event.key === "word") {
      await handleExportInfoCardWord();
    }
  }

  // 导出信息卡：PDF
  const handleExportInfoCardPdf = async () => {
    setExportLoading(true);
    try {
      if (!infoCardRef.current) {
        message.error("无法获取信息卡内容");
        return;
      }

      // 校验井盖编号是否存在
      if (!currentValve.jgbh) {
        message.error("井盖编号未填写，无法生成");
        setExportLoading(false);
        return;
      }

      // 确保所有图片已完全加载
      const images = infoCardRef.current.querySelectorAll("img");
      await Promise.all(
        [...images].map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve; // 即使图片加载失败也继续
          });
        })
      );

      const canvas = await html2canvas(infoCardRef.current, {
        scale: 2, // 适当降低scale以避免内存问题
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 15000, // 增加图片加载超时时间
        removeContainer: true,
        allowTaint: false,
        onclone: (clonedDoc) => {
          // 确保克隆文档中的图片保持原始比例
          const clonedGisImages = clonedDoc.querySelectorAll(".gis-image img");
          clonedGisImages.forEach((img) => {
            img.style.objectFit = "cover"; // 使用contain保持图片比例
            img.style.width = "90%";
            img.style.height = "140px"; // 固定高度
            img.style.maxHeight = "140px";
            img.style.display = "block";
            img.style.margin = "auto"; // 居中显示
          });

          const clonedjgImages = clonedDoc.querySelectorAll(".jg-image img");
          clonedjgImages.forEach((img) => {
            img.style.objectFit = "cover";
            img.style.Width = "80%";
            img.style.maxHeight = "260px";
            img.style.display = "block";
            img.style.margin = "3px";
            img.style.marginLeft = "30px";
          });
        },
      });

      const imgData = canvas.toDataURL("image/jpeg", 1.0); // 使用最高质量
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // 保持原始比例，居中显示
      const pdfWidth = 210; // A4纸宽度(mm)
      const pdfHeight = 297; // A4纸高度(mm)

      // 计算图像应该的尺寸，保持原始比例
      const imgRatio = canvas.height / canvas.width;
      let imgWidth = pdfWidth;
      let imgHeight = pdfWidth * imgRatio;

      // 如果高度超出A4纸，则按高度缩放
      if (imgHeight > pdfHeight) {
        imgHeight = pdfHeight;
        imgWidth = pdfHeight / imgRatio;
      }

      // 计算居中位置
      const x = (pdfWidth - imgWidth) / 2;
      const y = (pdfHeight - imgHeight) / 2;

      pdf.addImage(imgData, "JPEG", x, y, imgWidth, imgHeight);
      // 使用井盖编号作为文件名
      pdf.save(`${currentValve.jgbh}阀门信息卡.pdf`);
      message.success("导出信息卡成功");
    } catch (error) {
      console.error("导出信息卡失败:", error);
      message.error(
        "导出信息卡失败: " + error.response?.data?.message || error.message
      );
    } finally {
      setExportLoading(false);
    }
  };

  // 导出信息卡：Word
  const handleExportInfoCardWord = async () => {
    setExportLoading(true);

    const cardData = {
      ...wellCoverLocations.reduce((pv, cr, index) => {
        pv[`jgwz${index}`] = Number(currentValve.jgwz) === cr.value ? "☑" : "□";
        return pv;
      }, {}),
      ...wellCoverAbnormalDesc.reduce((pv, cr, index) => {
        pv[`jgycms${index}`] =
          Number(currentValve.jgycms) === cr.value ? "☑" : "□";
        return pv;
      }, {}),
      ...valveCategories.reduce((pv, cr, index) => {
        pv[`lb${cr.value}`] = currentValve.category === cr.value ? "☑" : "□";
        return pv;
      }, {}),
      ...valveTypes.reduce((pv, cr, index) => {
        pv[`type${cr.value}`] = currentValve.subtype === cr.value ? "☑" : "□";
        return pv;
      }, {}),

      pc_date: currentValve.pc_date
        ? new Date(currentValve.pc_date).toLocaleDateString("zh-CN")
        : "-",
      geometry_x: (currentValve.geometry_x && !hideCoordinates)
        ? Number(currentValve.geometry_x).toFixed(3)
        : "-",
      geometry_y: (currentValve.geometry_y && !hideCoordinates)
        ? Number(currentValve.geometry_y).toFixed(3)
        : "-",
      depth: currentValve.depth
        ? Number(currentValve.depth).toFixed(2) + "米"
        : "-",
      address: currentValve.address || "-",
      standard: currentValve.standard
        ? "DN" + Number(currentValve.standard)
        : "-",
      jgbh: currentValve.jgbh || "-",
      jgcj: currentValve.jgcj || "-",
      jgcc: currentValve.jgcc || "-",
      jgcz: currentValve.jgcz || "-",
      jgzt:
        wellCoverStatuses.find(
          (status) => status.value === Number(currentValve.jgzt)
        )?.label || "-",
      // 井圈周边 1123
      jgzb:
        wellCoverStatuses.find(
          (status) => status.value === Number(currentValve.jgzb)
        )?.label || "-",
      image_near: currentValve.jg_pic_near || "",
      image_far: currentValve.jg_pic_far || "",
      image_gis: currentValve.gis_pic || "",
    };

    const { data, filename } = await valveService.getInfoCard(cardData);
    const blob = new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // 修改文件命名格式，与PDF导出保持一致
    a.download = currentValve.jgbh ? `${currentValve.jgbh}阀门信息卡.docx` : (filename || "info_card.docx");
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    setExportLoading(false);
  };

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteValveId, setDeleteValveId] = useState(null);
  const [isDispatchModalVisible, setIsDispatchModalVisible] = useState(false);
  const [dispatchValve, setDispatchValve] = useState(null);
  const [userNames, setUserNames] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchNicknameOptions = async () => {
    try {
      const response = await userService.fetchNicknameOptions(2);
      if (response.code === 0) {
        setUserNames(response.data);
      } else {
        message.error(response.message || "获取用户姓名列表失败");
      }
    } catch (error) {
      message.error(
        "获取用户姓名列表失败：" + error.response?.data?.message ||
          error.message
      );
    }
  };

  useEffect(() => {
    fetchNicknameOptions();
  }, []);
  const handleDelete = async (valveId) => {
    setDeleteValveId(valveId);
    setIsDeleteModalVisible(true);
  };

  const handleBatchUpdateStatus = async () => {
    if (selectedRowKeys.length === 0) {
      message.error("请选择要修改的阀门");
      return;
    }

    setLoading(true);
    try {
      const response = await valveService.batchUpdateStatus(
        selectedRowKeys,
        batchStatus
      );

      if (response.code === 0) {
        message.success("批量修改状态成功");
        setIsBatchStatusModalVisible(false);
        setBatchStatus(null);
        // 直接在前端更新选中行的状态，而不是调用fetchValves()接口
        setData(
          data.map((item) => {
            if (selectedRowKeys.includes(item.id)) {
              return { ...item, status: batchStatus };
            }
            return item;
          })
        );
        // 清空选中的行
        setSelectedRowKeys([]);
      } else {
        message.error(response.message || "批量修改状态失败");
      }
    } catch (error) {
      message.error(
        "批量修改状态失败：" + error.response?.data?.message || error.message
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await valveService.removeValve(deleteValveId);

      if (response.code === 0) {
        message.success("删除阀门成功");
        fetchValves();
      } else {
        message.error(response.message || "删除阀门失败");
      }
    } catch (error) {
      message.error(
        "删除阀门失败：" + error.response?.data?.message || error.message
      );
    } finally {
      setIsDeleteModalVisible(false);
      setDeleteValveId(null);
    }
  };

  // 保存筛选条件和分页状态到sessionStorage
  const saveFilterToStorage = (filters, page = 1, size = pageSize) => {
    const filterState = {
      filters,
      page,
      pageSize: size,
      version: currentVersion,
      timestamp: new Date().getTime(),
    };
    sessionStorage.setItem("valveListState", JSON.stringify(filterState));
  };

  // 从sessionStorage恢复筛选条件和分页状态
  const restoreFilterFromStorage = () => {
    const stateStr = sessionStorage.getItem("valveListState");
    if (stateStr) {
      try {
        const state = JSON.parse(stateStr);
        // 恢复version
        if (state.version) {
          setCurrentVersion(state.version);
        }
        // 恢复筛选条件
        if (state.filters) {
          // 处理日期范围字段
          const filters = { ...state.filters };
          if (filters.pc_dateRange) {
            filters.pc_dateRange = filters.pc_dateRange.map((date) =>
              dayjs(date)
            );
          }
          form.setFieldsValue(filters);
          setCurrentSearchFilters(filters);
        }
        // 恢复分页状态
        if (state.page) setCurrentPage(state.page);
        if (state.pageSize) setPageSize(state.pageSize);

        // 使用恢复的条件执行搜索
        const searchParams = {
          ...state.filters,
          version: state.version,
          page: state.page,
          limit: state.pageSize,
          type: state.filters?.type === "all" ? undefined : state.filters?.type,
        };
        fetchValves(searchParams);
      } catch (error) {
        console.error("恢复筛选条件失败:", error);
      }
    }
  };

  const handleSearch = (values) => {
    const searchParams = {
      ...values,
      type: values.type === "all" ? undefined : values.type,
    };
    setCurrentPage(1);
    setCurrentSearchFilters(values);
    // 保存筛选条件到sessionStorage
    fetchValves(searchParams);
  };

  const handleReset = () => {
    form.resetFields();
    setCurrentPage(1);
    saveFilterToStorage({}, 1, pageSize);
    fetchValves();
  };

  const generateExcelFile = async (data, fileName) => {
    try {
      // 处理导出数据，按照指定字段顺序
      const processedData = data.map((item, index) => ({
        // 序号字段，从1开始递增
        index: item.id,
        // 普查日期
        pc_date: item.pc_date
          ? new Date(item.pc_date).toLocaleDateString("zh-CN")
          : "-",
        // 地址
        address: item.address || "-",
        // 横坐标
        geometry_x: item.geometry_x ? Number(item.geometry_x).toFixed(3) : "-",
        // 纵坐标
        geometry_y: item.geometry_y ? Number(item.geometry_y).toFixed(3) : "-",
        // 阀门深度
        depth: item.depth ? Number(item.depth).toFixed(2) + "米" : "-",
        // GIS流水号
        fmlsh: item.fmlsh || "-",
        // 阀门口径
        standard: item.standard ? "DN" + item.standard : "-",
        // 阀门类型
        subtype: (() => {
          const type = valveTypes.find(
            (type) => type.value === Number(item.subtype)
          );
          return type ? type.label : "-";
        })(),
        // 阀门类别
        category: (() => {
          const category = valveCategories.find(
            (cat) => cat.value === Number(item.category)
          );
          return category ? category.label : "-";
        })(),
        // 井盖编号
        jgbh: item.jgbh || "-",
        // 井盖厂家
        jgcj: item.jgcj || "-",
        // 井盖尺寸
        jgcc: item.jgcc || "-",
        // 井盖材质
        jgcz: item.jgcz || "-",
        // 井盖状态
        jgzt: (() => {
          const status = wellCoverStatuses.find(
            (status) => status.value === Number(item.jgzt)
          );
          return status ? status.label : "-";
        })(),
        // 井圈周边 1123
        jgzb: (() => {
          const status = wellCoverStatuses.find(
            (status) => status.value === Number(item.jgzb)
          );
          return status ? status.label : "-";
        })(),
        // 井盖位置
        jgwz: (() => {
          const location = wellCoverLocations.find(
            (location) => location.value === Number(item.jgwz)
          );
          return location ? location.label : "-";
        })(),
        // 井盖异常描述
        jgycms: (() => {
          const desc = wellCoverAbnormalDesc.find(
            (desc) => desc.value === Number(item.jgycms)
          );
          return desc ? desc.label : "-";
        })(),
        // 查验类别
        cylb: (() => {
          if (item.cylb === null) {
            return "-";
          } else {
            const type = checkTypes.find(
              (type) => type.value === Number(item.cylb)
            );
            return type ? type.label : "-";
          }
        })(),
        // 备注
        memo: item.memo || "-",
        // 现场情况（新增字段，默认为空）
        scene_situation: "",
        // 解决方案（新增字段，默认为空）
        solution: "",
      }));

      // 创建工作表
      const XLSX = require("xlsx");
      const ws = XLSX.utils.json_to_sheet(processedData);

      // 设置列宽
      const colWidths = [
        { wch: 8 }, // 序号
        { wch: 15 }, // 普查日期
        { wch: 25 }, // 地址
        { wch: 12 }, // 横坐标
        { wch: 12 }, // 纵坐标
        { wch: 12 }, // 阀门深度
        { wch: 15 }, // GIS流水号
        { wch: 10 }, // 阀门口径
        { wch: 12 }, // 阀门类型
        { wch: 20 }, // 阀门类别
        { wch: 15 }, // 井盖编号
        { wch: 15 }, // 井盖厂家
        { wch: 12 }, // 井盖尺寸
        { wch: 12 }, // 井盖材质
        { wch: 12 }, // 井盖状态
        { wch: 15 }, // 井圈周边
        { wch: 15 }, // 井盖位置
        { wch: 20 }, // 井盖异常描述
        { wch: 15 }, // 查验类别
        { wch: 25 }, // 备注
        { wch: 25 }, // 现场情况
        { wch: 25 }, // 解决方案
      ];
      ws["!cols"] = colWidths;

      // 设置表头
      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [
            "序号",
            "普查日期",
            "地址",
            "X坐标",
            "Y坐标",
            "阀门深度",
            "GIS流水号",
            "阀门口径",
            "阀门类型",
            "阀门类别",
            "井盖编号",
            "井盖厂家",
            "井盖尺寸",
            "井盖材质",
            "井盖状态",
            "井圈周边",
            "井盖位置",
            "井盖异常描述",
            "查验类别",
            "备注",
            "现场情况",
            "解决方案",
          ],
        ],
        { origin: "A1" }
      );

      // 创建工作簿并导出
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "阀门列表");
      XLSX.writeFile(wb, `${fileName}.xlsx`);

      return true;
    } catch (error) {
      message.error(
        "导出失败：" + error.response?.data?.message || error.message
      );
      return false;
    }
  };

  const handleBatchExport = async () => {
    if (selectedRowKeys.length === 0) {
      message.error("请先选择要导出的阀门");
      return;
    }

    setIsExportModalVisible(true);
  };

  const handleConfirmBatchExport = async () => {
    if (!exportFileName.trim()) {
      message.error("请输入文件名");
      return;
    }

    setLoading(true);
    try {
      // 从当前数据中筛选出选中的行
      const selectedData = data.filter((item) =>
        selectedRowKeys.includes(item.key)
      );

      // 使用抽象出的函数生成Excel文件
      const success = await generateExcelFile(selectedData, exportFileName);

      if (success) {
        setIsExportModalVisible(false);
        setExportFileName("");
        message.success(`成功导出${selectedData.length}条记录`);
      }
    } catch (error) {
      message.error(
        "批量导出失败：" + error.response?.data?.message || error.message
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!exportFileName.trim()) {
      message.error("请输入文件名");
      return;
    }
    setLoading(true);
    try {
      const response = await valveService.getValveList({
        page: 1,
        limit: 5000,
        export: true,
        version: currentVersion,
        subtype:
          currentSearchFilters?.subtype === "all"
            ? undefined
            : currentSearchFilters?.subtype,
        address: currentSearchFilters?.address,
        pc_date: currentSearchFilters?.pc_date,
        pc_date_start: currentSearchFilters?.pc_dateRange?.[0],
        pc_date_end: currentSearchFilters?.pc_dateRange?.[1].endOf("day"),
        geometry_x: currentSearchFilters?.geometry_x,
        geometry_y: currentSearchFilters?.geometry_y,
        geometry_z: currentSearchFilters?.geometry_z,
        fmlsh: currentSearchFilters?.fmlsh,
        status: currentSearchFilters?.status
          ? JSON.stringify(currentSearchFilters.status)
          : undefined,
        category: currentSearchFilters?.category
          ? JSON.stringify(currentSearchFilters.category)
          : undefined,
        jgbh: currentSearchFilters?.jgbh,
        jgzt: currentSearchFilters?.jgzt,
        jgzb: currentSearchFilters?.jgzb,
        jgycms: currentSearchFilters?.jgycms
          ? JSON.stringify(currentSearchFilters.jgycms)
          : undefined,
        cylb: currentSearchFilters?.cylb,
        is_deleted: 0,
      });

      // 使用抽象出的函数生成Excel文件
      const success = await generateExcelFile(
        response.data.list,
        exportFileName
      );

      if (success) {
        setIsExportModalVisible(false);
        setExportFileName("");
        message.success("导出成功");
      }
    } catch (error) {
      message.error(
        "导出失败：" + error.response?.data?.message || error.message
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <h2 style={{ margin: 0 }}>阀门管理</h2>
          <span style={{ marginLeft: "20px" }}>当前版本：{currentVersion}</span>
          <Button
            type="text"
            size="small"
            color="primary"
            variant="text"
            onClick={() => {
              setCurrentVersion(currentVersion === 2025 ? 2023 : 2025);
              fetchValves({ version: currentVersion === 2025 ? 2023 : 2025 });
            }}
          >
            切换{currentVersion === 2025 ? "2023年" : "2025年"}
          </Button>
        </div>
        <Space>
          <Button
            type="primary"
            onClick={() => navigate("/admin/valves/create")}
          >
            创建阀门
          </Button>
          <Button type="primary" onClick={() => setIsExportModalVisible(true)}>
            导出Excel
          </Button>
        </Space>
      </div>

      <FilterForm form={form} onFinish={handleSearch} onReset={handleReset}>
        {/* 始终显示的筛选条件（前8个，2行） */}
        <Form.Item span={2} name="pc_dateRange" label="普查日期">
          <DatePicker.RangePicker
            style={{ width: "100%" }}
            onChange={(dates) => {
              if (dates) {
                form.setFieldsValue({
                  pc_dateRange: dates,
                });
              }
            }}
          />
        </Form.Item>
        <Form.Item span={2} name="address" label="地址">
          <Input placeholder="请输入地址" />
        </Form.Item>

        <Form.Item name="fmlsh" label="GIS流水号">
          <Input placeholder="请输入GIS流水号" />
        </Form.Item>
        <Form.Item name="jgbh" label="井盖编号">
          <Input placeholder="请输入井盖编号" />
        </Form.Item>
        <Form.Item name="subtype" label="阀门类型">
          <Select
            placeholder="请选择阀门类型"
            allowClear
            style={{ width: "100%" }}
          >
            {valveTypes.map((type) => (
              <Select.Option key={type.value} value={type.value}>
                {type.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="status" label="阀门状态">
          <Select
            mode="multiple"
            placeholder="请选择阀门状态"
            options={valveStatuses}
            allowClear
          />
        </Form.Item>

        {/* 可折叠的筛选条件 */}

        <Form.Item name="category" label="阀门类别">
          <Select
            mode="multiple"
            placeholder="请选择阀门类别"
            options={valveCategories}
            allowClear
          />
        </Form.Item>
        <Form.Item name="geometry_x" label="x坐标">
          <Input placeholder="请输入x坐标" type="number" />
        </Form.Item>
        <Form.Item name="geometry_y" label="y坐标">
          <Input placeholder="请输入y坐标" type="number" />
        </Form.Item>
        <Form.Item name="geometry_z" label="z坐标">
          <Input placeholder="请输入z坐标" type="number" />
        </Form.Item>
        <Form.Item name="jgzt" label="井盖状态">
          <Select
            placeholder="请选择井盖状态"
            allowClear
            style={{ width: "100%" }}
          >
            {wellCoverStatuses.map((status) => (
              <Select.Option key={status.value} value={status.value}>
                {status.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="jgzb" label="井盖周圈">
          <Select
            placeholder="请选择井盖周圈状态"
            allowClear
            style={{ width: "100%" }}
          >
            {wellCoverStatuses.map((status) => (
              <Select.Option key={status.value} value={status.value}>
                {status.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="jgycms" label="井盖异常描述">
          <Select
            mode="multiple"
            placeholder="请选择异常描述"
            allowClear
            style={{ width: "100%" }}
          >
            {wellCoverAbnormalDesc.map((desc) => (
              <Select.Option key={desc.value} value={desc.value}>
                {desc.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="cylb" label="查验类别">
          <Select
            placeholder="请选择查验类别"
            allowClear
            style={{ width: "100%" }}
          >
            {cylbStatuses.map((status) => (
              <Select.Option key={status.value} value={status.value}>
                {status.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </FilterForm>

      {batchMode ? (
        <>
          <Button
            onClick={() => {
              setBatchMode(false);
              setSelectedRowKeys([]);
            }}
          >
            取消
          </Button>
          <Button
            style={{ marginLeft: 8 }}
            type="primary"
            disabled={selectedRowKeys.length === 0}
            onClick={() => setIsBatchStatusModalVisible(true)}
          >
            批量修改状态
          </Button>
          <Button
            style={{ marginLeft: 8 }}
            type="primary"
            disabled={selectedRowKeys.length === 0}
            onClick={handleBatchExport}
          >
            批量导出
          </Button>
        </>
      ) : (
        <Button onClick={() => setBatchMode(true)}>批量操作</Button>
      )}
      <div style={{ marginTop: "20px" }}>
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 1500 }}
          rowSelection={
            batchMode
              ? {
                  type: "checkbox",
                  selectedRowKeys,
                  onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
                }
              : undefined
          }
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
              fetchValves({ ...currentSearchFilters, page, limit: size });
            },
            onShowSizeChange: (current, size) => {
              setCurrentPage(current);
              setPageSize(size);
            },
            showTotal: (total) => `共 ${total} 条数据`,
            showSizeChanger: true,
          }}
        />
      </div>
      <Modal
        title="确认删除"
        open={isDeleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={() => {
          setIsDeleteModalVisible(false);
          setDeleteValveId(null);
        }}
        okText="确认"
        cancelText="取消"
      >
        <p>确定要删除这个阀门吗？</p>
      </Modal>

      <Modal
        title="导出阀门数据"
        open={isExportModalVisible}
        onCancel={() => {
          setIsExportModalVisible(false);
          setExportFileName("");
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsExportModalVisible(false);
              setExportFileName("");
            }}
          >
            取消
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={
              batchMode && selectedRowKeys.length > 0
                ? handleConfirmBatchExport
                : handleExport
            }
          >
            确认
          </Button>,
        ]}
      >
        <Form.Item label="文件名" required>
          <Input
            placeholder="请输入文件名"
            value={exportFileName}
            onChange={(e) => setExportFileName(e.target.value)}
          />
        </Form.Item>
      </Modal>

      {/* 派单弹窗 */}
      <Modal
        title="派单信息"
        open={isDispatchModalVisible}
        onOk={() => {
          if (!selectedUser) {
            message.error("请选择处理人");
            return;
          }
          handleDispatch(
            dispatchValve?.id,
            currentUser?.userId,
            selectedUser,
            dispatchValve?.gis_pic,
            dispatchValve?.location_pic
          );
          setIsDispatchModalVisible(false);
          setDispatchValve(null);
          setSelectedUser(null);
        }}
        onCancel={() => {
          setIsDispatchModalVisible(false);
          setDispatchValve(null);
        }}
        destroyOnClose
        okText="确认派单"
        cancelText="取消"
        width={550}
        okButtonProps={{ disabled: uploading }}
      >
        <div style={{ marginBottom: "16px" }}>
          <div>
            <strong>阀门ID：</strong>
            {dispatchValve?.id}
          </div>
          <div>
            <strong>创建人：</strong>
            {currentUser?.nickname || "-"}
          </div>
          <div style={{ padding: "8px " }}>
            <strong>GIS位置图：</strong>
            <PasteImage
              style={{ display: "inline-block" }}
              src={dispatchValve?.location_pic}
              onSuccess={(result) => {
                setDispatchValve((prev) => ({
                  ...prev,
                  location_pic: result.url,
                }));
              }}
              onUploadStatusChange={setUploading}
            ></PasteImage>
          </div>
          <div style={{ padding: "8px " }}>
            <strong>GIS平面图：</strong>
            <PasteImage
              style={{ display: "inline-block" }}
              src={dispatchValve?.gis_pic}
              onSuccess={(result) => {
                setDispatchValve((prev) => ({
                  ...prev,
                  gis_pic: result.url,
                }));
              }}
              onUploadStatusChange={setUploading}
            ></PasteImage>
          </div>

          <div>
            <strong>处理人：</strong>
            <Select
              style={{ width: "200px" }}
              placeholder="请选择处理人"
              value={selectedUser}
              onChange={(value) => setSelectedUser(value)}
            >
              {userNames.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.nickname}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </Modal>

      {/* 批量修改状态弹窗 */}
      <Modal
        title="批量修改状态"
        open={isBatchStatusModalVisible}
        onOk={() => {
          if (batchStatus === null) {
            message.error("请选择状态");
            return;
          }
          handleBatchUpdateStatus();
        }}
        onCancel={() => {
          setIsBatchStatusModalVisible(false);
          setBatchStatus(null);
        }}
        okText="确认"
        cancelText="取消"
      >
        <Form.Item label="选择状态" required>
          <Select
            placeholder="请选择状态"
            value={batchStatus}
            onChange={(value) => setBatchStatus(value)}
            style={{ width: "100%" }}
          >
            {valveStatuses.map((status) => (
              <Select.Option key={status.value} value={status.value}>
                {status.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Modal>

      <Drawer
        className="info-card-drawer"
        title={
          <div className="info-card-header">
            <div className="info-card-title">信息卡预览</div>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <Radio.Group
                defaultValue={false}
                onChange={(e) => setHideCoordinates(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value={false}>不隐藏坐标</Radio.Button>
                <Radio.Button value={true}>隐藏坐标</Radio.Button>
              </Radio.Group>
              <Dropdown menu={{ items: exportMenuItems, onClick: handleExportInfoCard }}>
                <Button type="primary" loading={exportLoading}>
                  导出信息卡<DownOutlined />
                </Button>
              </Dropdown>
            </div>
          </div>
        }
        placement="right"
        onClose={() => setIsDrawerVisible(false)}
        open={isDrawerVisible}
        width={700}
        styles={{
          body: {
            background: "#f2f2f2",
          },
        }}
      >
        <div ref={infoCardRef} className="info-card-content">
          <div className="info-card-main-title">
            常州通用自来水有限公司阀门及井盖信息卡
          </div>
          <div className="info-card-date">
            普查日期:{" "}
            {currentValve?.pc_date
              ? new Date(currentValve.pc_date)
                  .toLocaleDateString("zh-CN", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  })
                  .replace(/\//g, "")
              : "-"}
          </div>
          <div className="info-card-table gis-table">
            {/* GIS平面图 */}
            <div className="info-card-line table-header">GIS平面图</div>
            <div className="info-card-line gis-image">
              {currentValve?.gis_pic ? (
                <img src={currentValve.gis_pic} alt="GIS平面图" />
              ) : (
                <span>暂无GIS平面图</span>
              )}
            </div>
            {/* 近景远景 */}
            <div className="info-card-line">
              <div className="info-card-block table-header">
                照片（井内近景）
              </div>
              <div className="info-card-block table-header">
                照片（井盖远景）
              </div>
            </div>
            <div className="info-card-line grow-line">
              <div className="info-card-block jg-image">
                {currentValve?.jg_pic_near ? (
                  <img src={currentValve.jg_pic_near} alt="井内近景照片" />
                ) : (
                  <span>暂无井内近景照片</span>
                )}
              </div>
              <div className="info-card-block jg-image">
                {currentValve?.jg_pic_far ? (
                  <img src={currentValve.jg_pic_far} alt="井盖远景照片" />
                ) : (
                  <span>暂无井盖远景照片</span>
                )}
              </div>
            </div>

            {/* 详细信息 */}
            <div className="info-card-line details-row">
              地址：{currentValve?.address || ""}
            </div>
            <div className="info-card-line ">
              <div className="info-card-block details-row">
                X坐标：
                <span>
                  {hideCoordinates
                    ? ""
                    : currentValve?.geometry_x
                    ? Number(currentValve.geometry_x).toFixed(3)
                    : ""}
                </span>
              </div>
              <div className="info-card-block details-row">
                Y坐标：
                <span>
                  {hideCoordinates
                    ? ""
                    : currentValve?.geometry_y
                    ? Number(currentValve.geometry_y).toFixed(3)
                    : ""}
                </span>
              </div>
            </div>
            <div className="info-card-line">
              <div className="info-card-block details-row">
                阀门深度：
                <span>
                  {currentValve?.jgycms === 2
                    ? ""
                    : currentValve?.depth + "m" || ""}
                </span>
              </div>
              <div className="info-card-block details-row">
                阀门口径：<span>DN{currentValve?.standard || ""}</span>
              </div>
            </div>
            <div className="info-card-line details-row">
              阀门类型：
              {valveTypes.map((subtype) => (
                <div key={subtype.value} className="checkbox-item">
                  <img
                    src={
                      currentValve?.subtype === subtype.value
                        ? "https://cdn.czwatersystem.com/static_res/selected_checkbox..jpg"
                        : "https://cdn.czwatersystem.com/static_res/unselected_checkbox.jpg"
                    }
                    alt="checkbox"
                  />
                  {subtype.label}
                </div>
              ))}
            </div>
            <div className="info-card-line details-row checkbox-row">
              <div className="checkbox-label">阀门类别：</div>
              <div className="checkbox-items">
                {valveCategories.map((category) => (
                  <span key={category.value} className="checkbox-item">
                    <img
                      src={
                        currentValve?.category === category.value
                          ? "https://cdn.czwatersystem.com/static_res/selected_checkbox..jpg"
                          : "https://cdn.czwatersystem.com/static_res/unselected_checkbox.jpg"
                      }
                      alt="checkbox"
                    />
                    {category.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="info-card-line">
              <div className="info-card-block details-row">
                井盖编号：<span>{currentValve?.jgbh || ""}</span>
              </div>
              <div className="info-card-block details-row">
                井盖厂家：
                <span>
                  {currentValve?.jgycms === 2 ? "" : currentValve?.jgcj || ""}
                </span>
              </div>
            </div>
            <div className="info-card-line">
              <div className="info-card-block details-row">
                井盖尺寸：
                <span>
                  {currentValve?.jgycms === 2 ? "" : currentValve?.jgcc || ""}
                </span>
              </div>
              <div className="info-card-block details-row">
                井盖材质：
                <span>
                  {currentValve?.jgycms === 2 ? "" : currentValve?.jgcz || ""}
                </span>
              </div>
            </div>
            <div className="info-card-line">
              <div className="info-card-block details-row">
                井盖状态：
                <span>
                  {currentValve?.jgycms === 2
                    ? ""
                    : currentValve?.jgzt === null ||
                      currentValve?.jgzt === undefined
                    ? ""
                    : currentValve?.jgzt === 1
                    ? "完好"
                    : "破损"}
                </span>
              </div>
              <div className="info-card-block details-row">
                井盖周边：
                <span>
                  {currentValve?.jgycms === 2
                    ? ""
                    : currentValve?.jgzb === null ||
                      currentValve?.jgzb === undefined
                    ? ""
                    : currentValve?.jgzb === 1
                    ? "完好"
                    : "破损"}
                </span>
              </div>
            </div>
            <div className="info-card-line details-row checkbox-row">
              <div className="checkbox-label">井盖位置：</div>
              <div className="checkbox-items">
                {wellCoverLocations.map((jgwz) => (
                  <div key={jgwz.value} className="checkbox-item">
                    <img
                      src={
                        currentValve?.jgycms === 2
                          ? "https://cdn.czwatersystem.com/static_res/unselected_checkbox.jpg"
                          : currentValve?.jgwz === jgwz.value
                          ? "https://cdn.czwatersystem.com/static_res/selected_checkbox..jpg"
                          : "https://cdn.czwatersystem.com/static_res/unselected_checkbox.jpg"
                      }
                      alt="checkbox"
                    />
                    {jgwz.label}
                  </div>
                ))}
              </div>
            </div>
            <div className="info-card-line details-row checkbox-row">
              <div className="checkbox-label">井盖异常描述：</div>
              <div className="checkbox-items">
                {wellCoverAbnormalDesc.map((jgycms) => (
                  <div key={jgycms.value} className="checkbox-item">
                    <img
                      src={
                        currentValve?.jgycms === jgycms.value
                          ? "https://cdn.czwatersystem.com/static_res/selected_checkbox..jpg"
                          : "https://cdn.czwatersystem.com/static_res/unselected_checkbox.jpg"
                      }
                      alt="checkbox"
                    />
                    {jgycms.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
}

export default Valves;
